import React, { FC } from 'react'
import { useAnalyticsHandlers } from '@moonpig/launchpad-analytics'
import { useSearchAnalytics } from '../../../../pages/SearchPage/analytics'
import { sortByEvent } from '../../../../pages/SearchPage/events'
import { useOptionalSearchStore } from '../../../../store/SearchStore'
import { getTrackingLabelForSortOrder } from '../../../../utils/getTrackingLabel'
import { Item } from '@radix-ui/react-dropdown-menu'
import {
  FormControlLabel,
  RadioButton,
  RadioGroup,
} from '@moonpig/launchpad-forms'
import { styled } from '@moonpig/launchpad-utils'

const StyledFormControlLabel = styled(FormControlLabel)`
  padding: 8px;
  height: 54px;
`

const StyledDropdownMenuItem = styled(Item)`
  margin: 1px;

  &:hover {
    outline: none;
  }

  &:nth-child(odd) {
    border-radius: 12px 12px 0 0;
  }

  &:nth-child(even) {
    border-radius: 0 0 12px 12px;
  }
`

type Props = {
  options: { label: string; value: string }[]
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  disabled: boolean
  ariaLabelledBy: string
  inDropdownMenu?: boolean
}

const SortOption: FC<
  Omit<Props, 'options'> & { option: { label: string; value: string } }
> = ({ option, value, onChange, disabled, inDropdownMenu }) => {
  const isLaunchpadAnalytics = useSearchAnalytics()

  const pageType = useOptionalSearchStore(
    store => store.pageContext.type,
    'search',
  )

  const label = getTrackingLabelForSortOrder(option.value)

  // Legacy event: apps/explore/find/src/store/middleware/trackEvent.ts
  const sortChangeEvent = sortByEvent()
    .withEventData({
      category: `${pageType} action`.toLowerCase(),
      label: label.toLowerCase(),
    })
    .withContentData({
      content_type: `${pageType} action | sort by | ${label}`
        .toLowerCase()
        .substring(0, 100),
    })

  const selectOption = () =>
    onChange({
      target: { value: option.value },
      currentTarget: { value: option.value },
    } as React.ChangeEvent<HTMLInputElement>)

  const selectHandlers = useAnalyticsHandlers(
    isLaunchpadAnalytics ? { onSelect: sortChangeEvent } : {},
    { onSelect: selectOption },
  )

  const radio = (
    <StyledFormControlLabel
      label={option.label}
      id={option.value}
      disabled={disabled}
    >
      <RadioButton
        name="sort-by"
        checked={value === option.value}
        events={
          isLaunchpadAnalytics && !inDropdownMenu
            ? { onChange: sortChangeEvent }
            : undefined
        }
        onChange={inDropdownMenu && isLaunchpadAnalytics ? () => {} : onChange}
        value={option.value}
      />
    </StyledFormControlLabel>
  )

  return inDropdownMenu ? (
    <StyledDropdownMenuItem
      textValue={option.label}
      disabled={disabled}
      onSelect={event =>
        selectHandlers.onSelect?.(
          event as unknown as React.SyntheticEvent<HTMLElement>,
        )
      }
    >
      {radio}
    </StyledDropdownMenuItem>
  ) : (
    radio
  )
}

export const SortOptions: FC<Props> = ({ options, ...props }) => {
  const items = options.map(option => (
    <SortOption key={option.value} option={option} {...props} />
  ))

  return props.inDropdownMenu ? (
    <div aria-labelledby={props.ariaLabelledBy}>{items}</div>
  ) : (
    <RadioGroup name="sort-by" ariaLabelledBy={props.ariaLabelledBy}>
      {items}
    </RadioGroup>
  )
}
